import React from 'react';
import type { ComponentPropsWithoutRef, MouseEvent } from 'react';

import PropTypes from 'prop-types';

import { DiskBadge } from 'apps/promo/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/promo/components/common/Icon/Icon';
import type { IconColor } from 'apps/promo/components/common/Icon/Icon';

import { CommonButton, DiskBadgeWrap, IconWrap, SubmitButton } from './styles';
import { Text } from '../Text/Text';
import type { TextColor } from '../Text/Text';

type PopupButtonType = 'submit' | 'link' | 'reactLink';

type AnchorButtonProps = ComponentPropsWithoutRef<'a'> & {
    isAlertButton?: boolean;
    isCentered?: boolean;
    'data-testid'?: string;
    disabled?: boolean;
};

type SubmitButtonStyledProps = ComponentPropsWithoutRef<'button'> & {
    isAlertButton?: boolean;
};

const AnchorButton = CommonButton as unknown as React.ComponentType<AnchorButtonProps>;
const SubmitButtonStyled = SubmitButton as unknown as React.ComponentType<SubmitButtonStyledProps>;

export interface PopupButtonProps {
    type?: PopupButtonType;
    href?: string;
    rel?: string;
    title?: string;
    target?: string;
    id?: string;
    dataTestId?: string;
    isAlertButton?: boolean;
    onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
    iconName?: string;
    text?: string;
    tabIndex?: number;
    value?: string;
    buttonForm?: string;
    disabled?: boolean;
    isCentered?: boolean;
    hasDiskBadgeIcon?: boolean;
}

type PopupColor = 'darkGray' | 'purpleRgba40' | 'purple' | 'red';

const getColor = (hasDiskBadgeIcon?: boolean, disabled?: boolean, isAlertButton?: boolean): PopupColor => {
    if (hasDiskBadgeIcon) {
        return 'darkGray';
    }
    if (disabled) {
        return 'purpleRgba40';
    }
    return isAlertButton ? 'red' : 'purple';
};

const renderIcon = (color: IconColor, iconName?: string, hasDiskBadgeIcon?: boolean) => {
    return hasDiskBadgeIcon ? (
        <DiskBadgeWrap>
            <DiskBadge iconName={iconName} variant="secondary" />
        </DiskBadgeWrap>
    ) : (
        <IconWrap>
            <Icon name={iconName ?? ''} color={color} size="24px" />
        </IconWrap>
    );
};

const PopupButton = ({
    type,
    href,
    rel,
    title,
    target,
    id,
    dataTestId,
    isAlertButton,
    onClick,
    iconName,
    text,
    tabIndex,
    value,
    buttonForm,
    disabled,
    isCentered,
    hasDiskBadgeIcon,
}: PopupButtonProps) => {
    const color = getColor(hasDiskBadgeIcon, disabled, isAlertButton);
    const iconColor = color as IconColor;
    const textColor = color as TextColor;

    if (type === 'submit') {
        return (
            <SubmitButtonStyled
                id={id}
                type="submit"
                tabIndex={tabIndex}
                form={buttonForm}
                isAlertButton={isAlertButton}
                value={value}
                onClick={onClick}
                disabled={disabled}
            >
                {renderIcon(iconColor, iconName, hasDiskBadgeIcon)}
                <Text ellipsis color={textColor}>
                    {text}
                </Text>
            </SubmitButtonStyled>
        );
    }

    return (
        <AnchorButton
            href={href}
            rel={rel}
            title={title}
            target={target}
            id={id}
            data-testid={dataTestId}
            isAlertButton={isAlertButton}
            onClick={onClick}
            disabled={disabled}
            isCentered={isCentered}
        >
            {renderIcon(iconColor, iconName, hasDiskBadgeIcon)}
            <Text ellipsis color={textColor}>
                {text}
            </Text>
        </AnchorButton>
    );
};

export default PopupButton;

PopupButton.propTypes = {
    type: PropTypes.string,
    href: PropTypes.string,
    rel: PropTypes.string,
    title: PropTypes.string,
    target: PropTypes.string,
    id: PropTypes.string,
    dataTestId: PropTypes.string,
    isAlertButton: PropTypes.bool,
    onClick: PropTypes.func,
    iconName: PropTypes.string,
    text: PropTypes.string,
    tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.string,
    buttonForm: PropTypes.string,
    disabled: PropTypes.bool,
    isCentered: PropTypes.bool,
    hasDiskBadgeIcon: PropTypes.bool,
};
